import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import type { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types'

type Client = Tables<'clients'>
type ClientInsert = TablesInsert<'clients'>
type ClientUpdate = TablesUpdate<'clients'>

export function useClients() {
  return useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as Client[]
    },
  })
}

export function useClient(id: string) {
  return useQuery({
    queryKey: ['clients', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('id', id)
        .maybeSingle()

      if (error) throw error
      return data as Client | null
    },
    enabled: !!id,
  })
}

export function useCreateClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (client: ClientInsert & { hourly_rate?: number | null; notes?: string | null }) => {
      const { data, error } = await supabase
        .from('clients')
        .insert(client as ClientInsert)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
    },
  })
}

export function useUpdateClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...updates }: ClientUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from('clients')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      queryClient.invalidateQueries({ queryKey: ['clients', variables.id] })
    },
  })
}

export function useDeleteClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
    },
  })
}

// Helper to get all client stats at once (more efficient than per-client queries)
export function useAllClientStats() {
  return useQuery({
    queryKey: ['clients', 'all-stats'],
    queryFn: async () => {
      // Buscar projetos com dados de revenue da view
      const { data: projectRevenue, error: revenueError } = await supabase
        .from('project_revenue')
        .select('id, name, status, revenue, hours_spent, billing_type, fixed_price')

      if (revenueError) throw revenueError

      // Buscar projetos para pegar o client_id (a view não tem)
      const { data: projects, error: projectsError } = await supabase
        .from('projects')
        .select('id, client_id, status')

      if (projectsError) throw projectsError

      // Mapear project_id para client_id
      const projectClientMap = new Map<string, string>()
      projects?.forEach(p => {
        if (p.client_id) {
          projectClientMap.set(p.id, p.client_id)
        }
      })

      // Agregar stats por cliente
      const clientStatsMap: Record<string, {
        activeProjects: number
        totalProjects: number
        totalRevenue: number
        totalHours: number
      }> = {}

      projectRevenue?.forEach(pr => {
        const clientId = projectClientMap.get(pr.id || '')
        if (!clientId) return

        if (!clientStatsMap[clientId]) {
          clientStatsMap[clientId] = {
            activeProjects: 0,
            totalProjects: 0,
            totalRevenue: 0,
            totalHours: 0,
          }
        }

        clientStatsMap[clientId].totalProjects += 1
        clientStatsMap[clientId].totalRevenue += Number(pr.revenue) || 0
        clientStatsMap[clientId].totalHours += Number(pr.hours_spent) || 0

        if (pr.status !== 'done') {
          clientStatsMap[clientId].activeProjects += 1
        }
      })

      return clientStatsMap
    },
  })
}

// Helper to get single client stats
export function useClientStats(clientId: string) {
  return useQuery({
    queryKey: ['clients', clientId, 'stats'],
    queryFn: async () => {
      // Buscar projetos do cliente com dados da view project_revenue
      const { data: projects, error: projectsError } = await supabase
        .from('projects')
        .select('id, status')
        .eq('client_id', clientId)

      if (projectsError) throw projectsError

      const projectIds = projects?.map(p => p.id) || []
      
      if (projectIds.length === 0) {
        return {
          activeProjects: 0,
          totalProjects: 0,
          totalRevenue: 0,
          totalHours: 0,
        }
      }

      // Buscar dados de revenue da view
      const { data: revenueData, error: revenueError } = await supabase
        .from('project_revenue')
        .select('id, revenue, hours_spent, status')
        .in('id', projectIds)

      if (revenueError) throw revenueError

      const activeProjects = revenueData?.filter(p => p.status !== 'done').length || 0
      const totalRevenue = revenueData?.reduce((acc, p) => acc + (Number(p.revenue) || 0), 0) || 0
      const totalHours = revenueData?.reduce((acc, p) => acc + (Number(p.hours_spent) || 0), 0) || 0

      return {
        activeProjects,
        totalProjects: projects?.length || 0,
        totalRevenue,
        totalHours,
      }
    },
    enabled: !!clientId,
  })
}
